import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import MetricCard from './components/MetricCard';
import QuickActionCard from './components/QuickActionCard';
import ActivityFeedItem from './components/ActivityFeedItem';
import StudentPerformanceTable from './components/StudentPerformanceTable';

import Button from '../../components/ui/Button';
import { Users, Settings } from 'lucide-react';
import { RealtimeSubscriptionService } from '../../services/realtimeSubscriptionService';
import { supabase } from '../../lib/supabase';
import { 
  getAdminDashboardMetrics, 
  getRecentActivities, 
  getTopStudents,
  getSystemAlerts 
} from '../../services/adminDashboardService';
import { useAuth } from '../../contexts/AuthContext';

const quickActions = [
  {
    title: "Manage Users",
    description: "Add, edit, or remove student and educator accounts",
    icon: "Users",
    iconColor: "bg-primary",
    actionLabel: "Go to Users",
    actionPath: "/user-management"
  },
  {
    title: "Content Management",
    description: "Upload and organize plays, audio, and glossary terms",
    icon: "BookOpen",
    iconColor: "bg-accent",
    actionLabel: "Manage Content",
    actionPath: "/play-management"
  },
  {
    title: "Analytics",
    description: "View detailed reports on student performance and engagement",
    icon: "BarChart3",
    iconColor: "bg-success",
    actionLabel: "View Analytics",
    actionPath: "/analytics-dashboard"
  },
  {
    title: "System Settings",
    description: "Configure platform settings and preferences",
    icon: "Settings",
    iconColor: "bg-warning",
    actionLabel: "Open Settings",
    actionPath: "/site-settings"
  },
  {
    title: "Avatar Asset Studio",
    description: "Upload, manage, and organize Shakespearean avatar assets",
    icon: "Wand2",
    iconColor: "bg-purple-600",
    actionLabel: "Open Studio",
    actionPath: "/admin-avatar-asset-studio"
  },
  {
    title: "Badge & Achievement Analytics",
    description: "Unlock rates, distribution charts, and user progress toward badges",
    icon: "Award",
    iconColor: "bg-rose-500",
    actionLabel: "View Analytics",
    actionPath: "/badge-achievement-analytics"
  }
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [realtimeService] = useState(() => new RealtimeSubscriptionService());
  const [liveNotifications, setLiveNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [topStudents, setTopStudents] = useState([]);
  const [alerts, setAlerts] = useState([]);

  // Fetch dashboard data on mount
  useEffect(() => {
    async function fetchDashboardData() {
      try {
        setLoading(true);
        
        // Fetch metrics
        const metricsData = await getAdminDashboardMetrics();
        setMetrics([
          {
            title: "Total Students",
            value: metricsData?.totalStudents?.toString(),
            change: "+12.5%",
            changeType: "positive",
            trend: "up",
            icon: "Users",
            iconColor: "bg-primary"
          },
          {
            title: "Active Plays",
            value: metricsData?.activePlays?.toString(),
            change: "+2",
            changeType: "positive",
            trend: "up",
            icon: "Theater",
            iconColor: "bg-accent"
          },
          {
            title: "Quiz Completion Rate",
            value: metricsData?.completionRate,
            change: "+5.2%",
            changeType: "positive",
            trend: "up",
            icon: "CheckCircle",
            iconColor: "bg-success"
          },
          {
            title: "Avg. Session Time",
            value: metricsData?.avgSessionTime,
            change: "-3m",
            changeType: "negative",
            trend: "down",
            icon: "Clock",
            iconColor: "bg-warning"
          }
        ]);

        // Fetch activities
        const activities = await getRecentActivities();
        setRecentActivities(activities);

        // Fetch top students
        const students = await getTopStudents();
        setTopStudents(students);

        // Fetch alerts
        const systemAlerts = await getSystemAlerts();
        setAlerts(systemAlerts);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, []);

  // Set up real-time subscriptions for student activity
  useEffect(() => {
    const unsubscribe = realtimeService?.subscribeToStudentActivity(async (update) => {
      console.log('Real-time student activity update:', update);
      
      // Fetch user details for the notification
      let userName = 'A student';
      let userAvatar = null;
      
      if (update?.data?.user_id) {
        const { data: userProfile } = await supabase
          ?.from('user_profiles')
          ?.select('full_name, email')
          ?.eq('id', update?.data?.user_id)
          ?.single();
        
        if (userProfile) {
          userName = userProfile?.full_name || userProfile?.email;
        }
      }
      
      // Create notification based on activity type
      let notification = {
        id: `live-${Date.now()}-${Math.random()}`,
        timestamp: update?.timestamp || new Date(),
        read: false
      };
      
      if (update?.type === 'badge_earned') {
        notification = {
          ...notification,
          type: 'success',
          title: 'Badge Earned',
          message: `${userName} earned the "${update?.data?.badge_name}" badge`
        };
      } else if (update?.type === 'achievement_unlocked') {
        // Fetch achievement details
        const { data: achievement } = await supabase
          ?.from('achievements')
          ?.select('name, description')
          ?.eq('id', update?.data?.achievement_id)
          ?.single();
        
        notification = {
          ...notification,
          type: 'success',
          title: 'Achievement Unlocked',
          message: `${userName} unlocked "${achievement?.name || 'an achievement'}"`
        };
      } else if (update?.type === 'quiz_completed') {
        const score = update?.data?.score || 0;
        const totalQuestions = update?.data?.total_questions || 0;
        const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
        
        notification = {
          ...notification,
          type: update?.data?.passed ? 'success' : 'info',
          title: 'Quiz Completed',
          message: `${userName} completed a quiz with ${percentage}% score`
        };
      }
      
      // Add to live notifications
      setLiveNotifications(prev => [notification, ...prev]?.slice(0, 10));
    });
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [realtimeService]);

  const handleDismissAlert = (alertId) => {
    setAlerts(alerts?.filter((alert) => alert?.id !== alertId));
  };

  const handleAlertAction = (alert) => {
    navigate(alert?.actionPath);
  };

  // Combine live notifications with mock notifications
  const allNotifications = [...liveNotifications];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={user}
        isAuthenticated={true}
        notifications={allNotifications} />

      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbTrail />

          <div className="mb-6">
            <h1 className="text-4xl font-bold text-foreground mb-1">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Welcome back, {userProfile?.full_name || user?.email}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {metrics?.map((metric, index) =>
            <MetricCard key={index} {...metric} />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-foreground">Quick Actions</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {quickActions?.map((action, index) =>
                <QuickActionCard key={index} {...action} />
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-foreground">Recent Activity</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={async () => {
                    const activities = await getRecentActivities();
                    setRecentActivities(activities);
                  }}>

                  Refresh
                </Button>
              </div>
              <div className="bg-card border border-border rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
                {recentActivities?.length > 0 ? (
                  recentActivities?.map((activity) =>
                    <ActivityFeedItem key={activity?.id} activity={activity} />
                  )
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    No recent activities
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">Top Performing Students</h2>
              <Button
                variant="outline"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => navigate('/user-management')}>

                View All Students
              </Button>
            </div>
            {topStudents?.length > 0 ? (
              <StudentPerformanceTable students={topStudents} />
            ) : (
              <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
                No student data available
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
                  <span className="text-2xl">📚</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-card-foreground">Content Library</h3>
                  <p className="text-sm text-muted-foreground">8 plays available</p>
                </div>
              </div>
              <Button
                variant="outline"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => navigate('/play-management')}>

                Manage Plays
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-accent flex items-center justify-center">
                  <span className="text-2xl">📖</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-card-foreground">Glossary Terms</h3>
                  <p className="text-sm text-muted-foreground">342 definitions</p>
                </div>
              </div>
              <Button
                variant="outline"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => navigate('/glossary-management')}>

                Edit Glossary
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-success flex items-center justify-center">
                  <span className="text-2xl">⚙️</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-card-foreground">Site Settings</h3>
                  <p className="text-sm text-muted-foreground">Configure platform</p>
                </div>
              </div>
              <Button
                variant="outline"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => navigate('/site-settings')}>

                Open Settings
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>);

}